import { createContext, useContext, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  MODE_CORPORATE,
  MODE_RETAIL,
  DEFAULT_MODE,
  isNeutralPath,
  resolveMode,
} from "../lib/siteMode";
import "../styles/modeTransition.css";

const STORAGE_KEY = "siteMode";

const SiteModeContext = createContext(DEFAULT_MODE);

export function useSiteMode() {
  return useContext(SiteModeContext);
}

function readStickyMode() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

/**
 * Resolves the effective site mode for the current route and shares it via
 * context, so the navbar, footer, and theme all agree. Explicit vállalati /
 * lakossági routes set the mode; neutral pages (rólunk, impresszum,
 * adatkezelés) inherit the last explicit mode the visitor was in — persisted
 * in sessionStorage so it survives a reload of a neutral page too.
 *
 * Also owns the mode-switch transition: a full-screen wash in the destination
 * mode's brand color that fades in and back out in one smooth pulse —
 * softening the otherwise instant, jarring cut between the two very
 * different palettes. Driven imperatively via a ref (not state) so the
 * one-shot animation never fights a re-render.
 */
export default function SiteModeProvider({ children }) {
  const { pathname } = useLocation();
  const mode = resolveMode(pathname, readStickyMode());
  const overlayRef = useRef(null);
  const prevModeRef = useRef(mode);

  useLayoutEffect(() => {
    // Persist the mode only on explicit (non-neutral) pages, so neutral pages
    // inherit it rather than overwriting it with a fallback.
    if (!isNeutralPath(pathname)) {
      try {
        sessionStorage.setItem(STORAGE_KEY, mode);
      } catch {
        /* sessionStorage unavailable — sticky inheritance simply falls back */
      }
    }

    const modeChanged = prevModeRef.current !== mode;
    const overlay = overlayRef.current;

    // Flip the body class first, in the SAME synchronous pass as starting the
    // animation below — so the wash renders in the new mode's own colors
    // (the CSS vars it reads have already updated) from its very first frame.
    const { body, documentElement } = document;
    for (const el of [documentElement, body]) {
      el.classList.toggle(`mode-${MODE_CORPORATE}`, mode === MODE_CORPORATE);
      el.classList.toggle(`mode-${MODE_RETAIL}`, mode === MODE_RETAIL);
      el.dataset.mode = mode;
    }

    if (modeChanged && overlay) {
      // Restart the fade-in/fade-out pulse: drop the class, force a reflow so
      // the browser forgets the just-finished animation, then re-add it —
      // otherwise a rapid second switch wouldn't restart the animation.
      overlay.classList.remove("is-flashing");
      // eslint-disable-next-line no-unused-expressions
      overlay.offsetWidth;
      overlay.classList.add("is-flashing");
    }

    prevModeRef.current = mode;
  }, [mode, pathname]);

  return (
    <SiteModeContext.Provider value={mode}>
      <div ref={overlayRef} className="mode-transition-overlay" aria-hidden="true" />
      <div key={mode} className="mode-content-enter">
        {children}
      </div>
    </SiteModeContext.Provider>
  );
}
